import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePets } from '../context/PetContext';
import { useSlotPrices } from '../context/CatalogContext';
import { unlockSlot } from '../api/client';
import PetRenderer from '../components/PetRenderer';
import { ViewModeToggle } from '../components/visuals';
import { KennelIcon, HeartIcon, EnergyIcon, StarIcon } from '../components/icons';
import './Kennel.css';

const MAX_SLOTS = 10;

// Map mod cosmetic slot names to web attachment point names
const SLOT_MAP = {
  HAT: 'head', GLASSES: 'eyes', COLLAR: 'neck',
  CAPE: 'back', BACK_ITEM: 'back', WEAPON: 'hand',
  BOOTS: 'feet', TRAIL: 'trail',
  // Also accept lowercase/web format
  head: 'head', eyes: 'eyes', neck: 'neck',
  back: 'back', hand: 'hand', feet: 'feet', trail: 'trail',
};

function normalizeCosmetics(cosmetics) {
  if (!cosmetics) return {};
  const result = {};
  Object.entries(cosmetics).forEach(([slot, id]) => {
    if (!id) return;
    const webSlot = SLOT_MAP[slot] || slot.toLowerCase();
    result[webSlot] = id;
  });
  return result;
}

export default function Kennel() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { pets, userData, adoptPet, syncStatus, refreshData } = usePets();
  const SLOT_PRICES = useSlotPrices();
  const navigate = useNavigate();
  const [adoptError, setAdoptError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockCelebration, setUnlockCelebration] = useState(false);
  const [unlockMsg, setUnlockMsg] = useState('');

  const unlockedSlots = userData?.unlocked_slots ?? userData?.unlockedSlots ?? 2;

  const handleUnlockSlot = async () => {
    const nextSlot = unlockedSlots + 1;
    if (nextSlot > MAX_SLOTS) return;
    const cost = SLOT_PRICES[nextSlot - 1];
    setUnlocking(true);
    try {
      const res = await unlockSlot();
      setUnlockMsg(`Slot ${nextSlot} unlocked! 🎉`);
      setUnlockCelebration(true);
      setTimeout(() => { setUnlockCelebration(false); setUnlockMsg(''); }, 3000);
      refreshData();
    } catch (err) {
      setUnlockMsg(err.response?.data?.detail || 'Not enough treats');
      setTimeout(() => setUnlockMsg(''), 3000);
    }
    setUnlocking(false);
  };

  // Redirect to login
  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading) return <div className="kennel-loading">Loading...</div>;
  if (!isAuthenticated) return null;

  const emptySlots = MAX_SLOTS - pets.length;

  return (
    <div className="kennel-page">
      <div className="container">
        <div className="kennel-header">
          <KennelIcon size={32} color="var(--green-dark)" />
          <h1>My Kennel</h1>
          <p className="kennel-subtitle">
            {pets.length} of {MAX_SLOTS} slots used
            {syncStatus === 'syncing' && <span className="sync-badge">Syncing...</span>}
            {syncStatus === 'error' && <span className="sync-badge sync-error">Offline</span>}
          </p>
          <ViewModeToggle compact />
        </div>

        {unlockMsg && (
          <div className={`kennel-unlock-msg ${unlockCelebration ? 'success' : 'error'}`}>
            {unlockMsg}
          </div>
        )}

        <div className="kennel-grid">
          {pets.map((pet) => (
            <div
              key={pet.slot_index}
              className="kennel-slot card kennel-slot-filled"
              onClick={() => navigate(`/pets/${pet.slot_index}`)}
            >
              <div className="kennel-pet-preview">
                <PetRenderer
                  petType={pet.pet_type_id || pet.petTypeId}
                  variant={pet.variant_id || pet.variantId}
                  cosmetics={normalizeCosmetics(pet.equipped_cosmetics || pet.equippedCosmetics || {})}
                  cosmeticPositions={pet.cosmetic_positions || pet.cosmeticPositions || {}}
                  mood={(pet.mood || 'happy').toLowerCase()}
                  size={100}
                />
              </div>
              <h3 className="kennel-pet-name">{pet.pet_name || pet.petName || 'Pet'}</h3>
              <div className="kennel-pet-info">
                <span className="kennel-stat">
                  <StarIcon size={14} color="var(--yellow-warm)" /> Lv.{pet.level || 1}
                </span>
                <span className="kennel-stat">
                  <HeartIcon size={14} color="#E57373" /> {Math.round(Math.min(100, pet.happiness > 1 ? pet.happiness : pet.happiness * 100))}%
                </span>
                <span className="kennel-stat">
                  <EnergyIcon size={14} color="var(--orange-primary)" /> {Math.round(Math.min(100, pet.energy > 1 ? pet.energy : pet.energy * 100))}%
                </span>
              </div>
              <span className={`kennel-mood mood-${(pet.mood || 'happy').toLowerCase()}`}>
                {(pet.mood || 'happy').toLowerCase()}
              </span>
            </div>
          ))}

          {/* Empty unlocked slots */}
          {Array.from({ length: Math.max(0, unlockedSlots - pets.length) }).map((_, i) => (
            <div key={`empty-${i}`} className="kennel-slot card kennel-slot-empty"
                 onClick={() => navigate('/catalog')}>
              <div className="kennel-empty-icon">
                <img
                  src="/branding/petlings-pet-128.png"
                  alt=""
                  width={64}
                  height={64}
                  style={{ opacity: 0.32, filter: 'grayscale(0.35)' }}
                />
              </div>
              <span className="kennel-empty-text">Adopt a pet</span>
            </div>
          ))}

          {/* Locked slots - show price to unlock */}
          {unlockedSlots < MAX_SLOTS && (
            <div className={`kennel-slot card kennel-slot-locked ${unlockCelebration ? 'kennel-unlock-celebrate' : ''}`}
                 onClick={handleUnlockSlot}>
              <div className="kennel-lock-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect x="12" y="22" width="24" height="18" rx="3" fill="var(--text-muted)" opacity="0.3" />
                  <path d="M17 22V16C17 12.13 20.13 9 24 9C27.87 9 31 12.13 31 16V22" stroke="var(--text-muted)" strokeWidth="2.5" strokeLinecap="round" />
                  <circle cx="24" cy="31" r="3" fill="var(--text-muted)" opacity="0.5" />
                </svg>
              </div>
              <span className="kennel-lock-label">Unlock Slot {unlockedSlots + 1}</span>
              <span className="kennel-lock-price">🦴 {SLOT_PRICES[unlockedSlots]}</span>
              {unlocking && <span className="kennel-lock-loading">Unlocking...</span>}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
